import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Segmented,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import { toastr } from 'react-redux-toastr';
import { filterAccess } from 'components/Report/utils';
import {
  createHashtag,
  deleteHashtag,
  editHashtag,
  filterSocialPosts,
  getSocialStatus,
  importSocialPost,
  listHashtags,
  pollSocialNow,
  setSocialPostStatus,
} from 'state/actions/social';
import './SocialFeed.css';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'x', label: 'X' },
  { value: 'facebook', label: 'Facebook Page' },
];
const platformLabel = (p) => PLATFORMS.find((x) => x.value === p)?.label || p;
const PAGE_SIZE = 24;

const timeAgo = (d) => (d ? dayjs(d).format('DD MMM YYYY HH:mm') : '-');

/* ----------------------------- Hashtag manager ---------------------------- */

function HashtagManager({ siteId, status, hashtags, reload, canManage }) {
  const [hashtag, setHashtag] = useState('');
  const [platforms, setPlatforms] = useState(['instagram', 'x']);
  const [mediaOnly, setMediaOnly] = useState(true);
  const [saving, setSaving] = useState(false);

  const configured = status?.platforms || {};

  const handleAdd = async () => {
    const tag = hashtag.trim().replace(/^#+/, '');
    if (!tag) return;
    if (!platforms.length) {
      toastr.error('', 'Select at least one platform');
      return;
    }
    setSaving(true);
    try {
      await createHashtag({ siteId, hashtag: tag, platforms, mediaOnly });
      toastr.success('', `#${tag.toLowerCase()} added`);
      setHashtag('');
      reload();
    } catch (e) {
      toastr.error('', e.message);
    } finally {
      setSaving(false);
    }
  };

  const update = async (id, changes) => {
    try {
      await editHashtag({ siteId, id, ...changes });
      reload();
    } catch (e) {
      toastr.error('', e.message);
    }
  };

  const remove = async (id) => {
    try {
      await deleteHashtag({ siteId, id });
      reload();
    } catch (e) {
      toastr.error('', e.message);
    }
  };

  const stateTooltip = (t) =>
    (t.platforms || []).map((p) => {
      const s = t.state?.[p] || {};
      return (
        <div key={p}>
          <b>{platformLabel(p)}</b>:{' '}
          {!configured[p]
            ? 'not configured on server'
            : s.lastError
            ? `error - ${s.lastError}`
            : s.lastPolledAt
            ? `ok, ${timeAgo(s.lastPolledAt)}`
            : 'not polled yet'}
        </div>
      );
    });

  return (
    <div className="social-hashtags">
      {canManage && (
        <div className="social-hashtags__form">
          <Input
            prefix="#"
            placeholder="hashtag"
            value={hashtag}
            onChange={(e) => setHashtag(e.target.value.replace(/\s/g, ''))}
            onPressEnter={handleAdd}
            maxLength={100}
            className="social-hashtags__input"
          />
          <Checkbox.Group
            value={platforms}
            onChange={setPlatforms}
            options={PLATFORMS.map((p) => ({
              ...p,
              label: configured[p.value] ? p.label : `${p.label} (off)`,
            }))}
          />
          <Tooltip title="X only: skip posts without a photo or video">
            <span className="social-hashtags__media">
              <Switch size="small" checked={mediaOnly} onChange={setMediaOnly} />{' '}
              Media only
            </span>
          </Tooltip>
          <Button type="primary" loading={saving} onClick={handleAdd}>
            Add hashtag
          </Button>
        </div>
      )}

      {hashtags.length === 0 ? (
        <p className="social-muted">No hashtags tracked yet.</p>
      ) : (
        <div className="social-hashtags__list">
          {hashtags.map((t) => {
            const hasError = (t.platforms || []).some((p) => t.state?.[p]?.lastError);
            return (
              <div
                key={t._id}
                className={`social-hashtag ${t.isActive ? '' : 'is-inactive'}`}
              >
                <Tooltip title={stateTooltip(t)}>
                  <span className="social-hashtag__name">
                    #{t.hashtag}
                    {hasError && <span className="social-hashtag__dot" />}
                  </span>
                </Tooltip>
                <span className="social-hashtag__platforms">
                  {(t.platforms || []).map((p) => (
                    <Tag key={p} className={`social-tag social-tag--${p}`}>
                      {platformLabel(p)}
                    </Tag>
                  ))}
                </span>
                {t.pendingCount > 0 && (
                  <span className="social-hashtag__pending">{t.pendingCount} new</span>
                )}
                {canManage && (
                  <>
                    <Tooltip title={t.isActive ? 'Tracking' : 'Paused'}>
                      <Switch
                        size="small"
                        checked={t.isActive}
                        onChange={(v) => update(t._id, { isActive: v })}
                      />
                    </Tooltip>
                    <Popconfirm
                      title={`Stop tracking #${t.hashtag}?`}
                      description="Captured posts are kept."
                      onConfirm={() => remove(t._id)}
                    >
                      <button type="button" className="social-icon-btn" aria-label="Remove">
                        <i className="feather-trash-2" />
                      </button>
                    </Popconfirm>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {status && (
        <p className="social-muted social-hashtags__meta">
          Checked every {status.intervalMin} min
          {status.instagramProvider === 'apify' ? (
            <>
              {' '}· Instagram via Apify every {status.instagramIntervalMin} min (billed
              per result)
            </>
          ) : (
            <>
              {' '}· Instagram hashtags used {status.instagramHashtagsUsed}/
              {status.instagramHashtagLimit} (per 7 days, all sites) · Instagram only
              returns posts from the last 24 hours
            </>
          )}
          {status.lastRun?.finishedAt && (
            <> · Last check {timeAgo(status.lastRun.finishedAt)}</>
          )}
        </p>
      )}
    </div>
  );
}

/* -------------------------------- Post card ------------------------------- */

function PostCard({ post, selected, onSelect, onImport, onStatus, canCreate, canManage }) {
  const img = post.cachedImageUrl || post.imageUrl;
  const isVideo = !img && post.mediaUrl && /video/i.test(post.mediaType || '');
  return (
    <div className={`social-card ${selected ? 'is-selected' : ''}`}>
      <div className="social-card__media">
        {img ? (
          <img src={img} alt="" loading="lazy" />
        ) : isVideo ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={post.mediaUrl} muted preload="metadata" />
        ) : (
          <div className="social-card__noimg">No image</div>
        )}
        <Tag className={`social-tag social-tag--${post.platform} social-card__platform`}>
          {platformLabel(post.platform)}
        </Tag>
        {canManage && post.status !== 'imported' && (
          <Checkbox
            className="social-card__check"
            checked={selected}
            onChange={(e) => onSelect(post._id, e.target.checked)}
          />
        )}
      </div>
      <div className="social-card__body">
        <div className="social-card__meta">
          <span className="social-card__author">
            {post.author ? `@${post.author}` : post.authorName || 'Instagram user'}
          </span>
          <span className="social-muted">{timeAgo(post.postedAt)}</span>
        </div>
        <p className="social-card__caption">{post.caption || <i>No caption</i>}</p>
        <div className="social-card__tags">
          {(post.hashtags || []).map((h) => (
            <span key={h}>#{h}</span>
          ))}
        </div>
      </div>
      <div className="social-card__actions">
        {post.status === 'imported' ? (
          <Tag color="green">In Events</Tag>
        ) : (
          <>
            {canCreate && (
              <Button type="primary" size="small" onClick={() => onImport(post)}>
                Add to Events
              </Button>
            )}
            {canManage &&
              (post.status === 'ignored' ? (
                <Button size="small" onClick={() => onStatus([post._id], 'pending')}>
                  Restore
                </Button>
              ) : (
                <Button size="small" onClick={() => onStatus([post._id], 'ignored')}>
                  Ignore
                </Button>
              ))}
          </>
        )}
        {post.permalink && (
          <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="social-card__open">
            Open <i className="feather-external-link" />
          </a>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Import modal ------------------------------ */

function ImportModal({ siteId, post, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [preview, setPreview] = useState('');
  const [range, setRange] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!post) return;
    const caption = (post.caption || '').replace(/[{}<>]/g, '');
    setTitle(caption.split('\n')[0].slice(0, 80));
    setPreview(caption);
    setRange(null);
    setFile(null);
  }, [post]);

  const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    },
    [filePreview]
  );

  if (!post) return null;
  const img = post.cachedImageUrl || post.imageUrl;

  const submit = async () => {
    if (!title.trim()) {
      toastr.error('', 'Title is required');
      return;
    }
    if (!img && !file) {
      toastr.error('', 'This post has no image. Upload a cover image.');
      return;
    }
    setSaving(true);
    try {
      await importSocialPost({
        siteId,
        id: post._id,
        title: title.trim(),
        preview,
        start: range?.[0]?.toISOString(),
        end: range?.[1]?.toISOString(),
        status: 'publish',
        file,
      });
      toastr.success('', 'Post added to Events');
      onDone();
    } catch (e) {
      toastr.error('', e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!post}
      title={`Add ${platformLabel(post.platform)} post to Events`}
      onCancel={onClose}
      onOk={submit}
      okText="Add to Events"
      confirmLoading={saving}
      width={640}
      destroyOnClose
    >
      <div className="social-import">
        <div className="social-import__image">
          {filePreview || img ? (
            <img src={filePreview || img} alt="" />
          ) : (
            <div className="social-card__noimg">No image</div>
          )}
          <Upload
            accept="image/png,image/jpeg"
            showUploadList={false}
            beforeUpload={(f) => {
              setFile(f);
              return false;
            }}
          >
            <Button size="small">{file ? 'Change cover' : 'Upload cover'}</Button>
          </Upload>
        </div>
        <div className="social-import__fields">
          <label className="form-label">Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
          <label className="form-label">Description</label>
          <Input.TextArea
            value={preview}
            onChange={(e) => setPreview(e.target.value)}
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
          <label className="form-label">Schedule (optional)</label>
          <DatePicker.RangePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            value={range}
            onChange={setRange}
            style={{ width: '100%' }}
          />
          <p className="social-muted">
            Empty schedule = shown right away. Source link and author are saved on the event.
          </p>
        </div>
      </div>
    </Modal>
  );
}

/* --------------------------------- Feed ---------------------------------- */

function SocialFeed({ siteId, userData, onImported }) {
  const [status, setStatus] = useState(null);
  const [hashtags, setHashtags] = useState([]);
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [filters, setFilters] = useState({
    status: 'pending',
    platform: undefined,
    hashtag: undefined,
    search: '',
  });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(null);

  const canManage = filterAccess({ userData, siteId, param: 'event', action: 'update' });
  const canCreate = filterAccess({ userData, siteId, param: 'event', action: 'create' });

  const loadMeta = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([getSocialStatus(siteId), listHashtags(siteId)]);
      setStatus(s.data);
      setHashtags(h.data || []);
    } catch (e) {
      toastr.error('', e.message);
    }
  }, [siteId]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await filterSocialPosts({
        siteId,
        ...filters,
        page,
        limit: PAGE_SIZE,
      });
      setPosts(res.data || []);
      setTotal(res.total || 0);
      setSelected([]);
    } catch (e) {
      toastr.error('', e.message);
    } finally {
      setLoading(false);
    }
  }, [siteId, filters, page]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const setFilter = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  // Wait for a background poll (manual or cron) to finish, then refresh.
  const watchPoll = useCallback(
    (notifyResult) => {
      setPolling(true);
      const started = Date.now();
      const tick = async () => {
        try {
          const res = await getSocialStatus(siteId);
          setStatus(res.data);
          if (res.data.polling && Date.now() - started < 15 * 60 * 1000) {
            setTimeout(tick, 4000);
            return;
          }
          const run = res.data.lastRun;
          if (notifyResult && run) {
            toastr.success('', `${run.newCount} new post(s) found`);
            (run.errors || []).forEach((e) =>
              toastr.warning(`#${e.hashtag} · ${platformLabel(e.platform)}`, e.message)
            );
          }
        } catch (e) {
          toastr.error('', e.message);
        }
        setPolling(false);
        loadMeta();
        loadPosts();
      };
      setTimeout(tick, 2000);
    },
    [siteId, loadMeta, loadPosts]
  );

  // Resume the spinner if a poll is already running when the tab opens.
  useEffect(() => {
    if (status?.polling && !polling) watchPoll(false);
  }, [status?.polling]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePoll = async () => {
    try {
      const res = await pollSocialNow(siteId);
      toastr.success('', res.message);
      watchPoll(true);
    } catch (e) {
      toastr.error('', e.message);
    }
  };

  const handleStatus = async (ids, next) => {
    try {
      await setSocialPostStatus({ siteId, ids, status: next });
      loadPosts();
      loadMeta();
    } catch (e) {
      toastr.error('', e.message);
    }
  };

  const onSelect = (id, checked) =>
    setSelected((s) => (checked ? [...s, id] : s.filter((x) => x !== id)));

  const configuredCount = useMemo(
    () => Object.values(status?.platforms || {}).filter(Boolean).length,
    [status]
  );

  return (
    <div className="social-feed">
      <div className="social-feed__top">
        <div className="social-feed__platforms">
          {PLATFORMS.map((p) => (
            <Tooltip
              key={p.value}
              title={
                status?.platforms?.[p.value]
                  ? p.value === 'instagram' && status.instagramProvider === 'apify'
                    ? 'Configured (via Apify)'
                    : 'Credentials configured'
                  : 'Not configured on the server (.env)'
              }
            >
              <span
                className={`social-status ${
                  status?.platforms?.[p.value] ? 'is-on' : 'is-off'
                }`}
              >
                {p.label}
                {p.value === 'instagram' && status?.instagramProvider === 'apify' && (
                  <span className="social-status__via">Apify</span>
                )}
              </span>
            </Tooltip>
          ))}
        </div>
        {canManage && (
          <Button onClick={handlePoll} loading={polling} disabled={!configuredCount}>
            <i className="feather-refresh-cw" style={{ marginRight: 6 }} />
            {polling ? 'Checking...' : 'Check now'}
          </Button>
        )}
      </div>

      <HashtagManager
        siteId={siteId}
        status={status}
        hashtags={hashtags}
        reload={loadMeta}
        canManage={canManage}
      />

      <div className="social-feed__filters">
        <Segmented
          value={filters.status}
          onChange={(v) => setFilter('status', v)}
          options={[
            { value: 'pending', label: 'New' },
            { value: 'imported', label: 'Added' },
            { value: 'ignored', label: 'Ignored' },
          ]}
        />
        <Select
          allowClear
          placeholder="All platforms"
          value={filters.platform}
          onChange={(v) => setFilter('platform', v)}
          options={PLATFORMS}
          style={{ minWidth: 150 }}
        />
        <Select
          allowClear
          placeholder="All hashtags"
          value={filters.hashtag}
          onChange={(v) => setFilter('hashtag', v)}
          options={hashtags.map((h) => ({ value: h.hashtag, label: `#${h.hashtag}` }))}
          style={{ minWidth: 150 }}
        />
        <Input.Search
          allowClear
          placeholder="Search caption / author"
          onSearch={(v) => setFilter('search', v)}
          style={{ maxWidth: 260 }}
        />
        {canManage && selected.length > 0 && (
          <Button
            onClick={() =>
              handleStatus(selected, filters.status === 'ignored' ? 'pending' : 'ignored')
            }
          >
            {filters.status === 'ignored' ? 'Restore' : 'Ignore'} {selected.length} selected
          </Button>
        )}
        <span className="social-muted social-feed__count">{total} posts</span>
      </div>

      <Spin spinning={loading}>
        {posts.length === 0 && !loading ? (
          <Empty
            className="social-feed__empty"
            description={
              hashtags.length ? 'No posts here yet' : 'Add a hashtag to start collecting posts'
            }
          />
        ) : (
          <div className="social-grid">
            {posts.map((p) => (
              <PostCard
                key={p._id}
                post={p}
                selected={selected.includes(p._id)}
                onSelect={onSelect}
                onImport={setImporting}
                onStatus={handleStatus}
                canCreate={canCreate}
                canManage={canManage}
              />
            ))}
          </div>
        )}
      </Spin>

      {total > PAGE_SIZE && (
        <div className="social-feed__pagination">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}

      <ImportModal
        siteId={siteId}
        post={importing}
        onClose={() => setImporting(null)}
        onDone={() => {
          setImporting(null);
          loadPosts();
          loadMeta();
          if (onImported) onImported();
        }}
      />
    </div>
  );
}

export default SocialFeed;
